import { SxProps, Theme } from '@mui/material';

/** Edge where the inset primary bar appears on the selected item. */
export type SelectableItemSelectedAnchor = 'left' | 'bottom';

/** Layout direction for item content (`row` for lists, `column` for tab-like tiles). */
export type SelectableItemOrientation = 'row' | 'column';

const SELECTED_ANCHOR_INSET: Record<SelectableItemSelectedAnchor, string> = {
  left: '4px 0 0 0',
  bottom: '0 -4px 0 0',
};

/**
 * Base chrome for selectable list/tile items (master–detail / sidebar lists).
 * Hover lifts the item with a directional shadow while preserving its border.
 */
export const selectableItemRootSx: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'center',
  width: '100%',
  p: 1.5,
  gap: 1.5,
  border: 1,
  borderColor: 'divider',
  borderRadius: 1,
  bgcolor: 'background.paper',
  textAlign: 'left',
  color: 'text.primary',
  cursor: 'pointer',
  transition: 'all 0.15s ease-in-out',
  '&:hover': {
    bgcolor: 'action.hover',
    borderColor: 'divider',
    transform: 'translateY(-1px)',
    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
  },
};

/** Column layout for compact selectable tiles (e.g. platform icon + label). */
export const selectableItemColumnSx: SxProps<Theme> = {
  flexDirection: 'column',
  alignItems: 'center',
  textAlign: 'center',
};

/**
 * Selected state stays grounded and uses an inset anchor without layout shift.
 * @param anchor - Edge for the primary inset bar (`left` for list rows, `bottom` for tab-like tiles).
 */
export function getSelectableItemSelectedSx(
  anchor: SelectableItemSelectedAnchor = 'left',
): SxProps<Theme> {
  const inset = SELECTED_ANCHOR_INSET[anchor];
  const boxShadow = (theme: Theme) => `inset ${inset} ${theme.palette.primary.main}`;

  return {
    bgcolor: 'action.hover',
    borderColor: 'divider',
    boxShadow,
    cursor: 'default',
    transform: 'none',
    '&:hover': {
      bgcolor: 'action.hover',
      borderColor: 'divider',
      boxShadow,
      transform: 'none',
    },
  };
}

/** Default selected styles (left inset bar) for list rows. */
export const selectableItemSelectedSx = getSelectableItemSelectedSx('left');
